import {
  BufferingIndicator,
  Container,
  Controls,
  FullscreenButton,
  Gesture,
  Hotkey,
  MuteButton,
  PlayButton,
  Poster,
  Time,
  TimeSlider,
  VolumeSlider,
} from '@videojs/react';
import {
  FullscreenEnterIcon,
  FullscreenExitIcon,
  PauseIcon,
  PlayIcon,
  VolumeHighIcon,
  VolumeLowIcon,
  VolumeOffIcon,
} from '@videojs/react/icons';
import { Video, VideoPlayer as VideoJsPlayer } from '@videojs/react/video';
import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './VideoPlayer.module.scss';

/** How long the controls stay on screen after the last pointer or key event. */
const CONTROLS_IDLE_MS = 3000;

type VideoPlayerProps = {
  src: string;
  poster?: string;
  /** Shown in the player chrome when provided. */
  title?: string;
  /** Name of the control bar for screen readers. */
  controlsLabel: string;
  className?: string;
  /**
   * The small corner card: keeps play, mute, seek and time, drops the
   * controls that need room or a full window.
   */
  compact?: boolean;
};

/**
 * The app video player.
 *
 * Video.js 10 supplies the playback state, the keyboard shortcuts, the
 * slider maths and the ARIA wiring as unstyled parts. The interface is ours:
 * no packaged skin, so no skin stylesheet and no cascade layers to fight the
 * app's global button reset.
 *
 * Every control renders one element and takes its content from the `render`
 * prop, which keeps the props, the ARIA attributes and the state data
 * attributes the parts provide.
 */
export const VideoPlayer = ({
  src,
  poster,
  title,
  controlsLabel,
  className,
  compact = false,
}: VideoPlayerProps) => {
  const [controlsActive, setControlsActive] = useState(true);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // Any pointer or key event wakes the bar and restarts its idle countdown.
  const wakeControls = useCallback(() => {
    setControlsActive(true);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(
      () => setControlsActive(false),
      CONTROLS_IDLE_MS,
    );
  }, []);

  const sleepControls = useCallback(() => {
    clearTimeout(idleTimer.current);
    setControlsActive(false);
  }, []);

  useEffect(() => {
    wakeControls();
    return () => clearTimeout(idleTimer.current);
  }, [wakeControls]);

  return (
    <VideoJsPlayer title={title} poster={poster}>
      <Container
        className={[styles.container, compact && styles.compact, className]
          .filter(Boolean)
          .join(' ')}
        data-controls-idle={controlsActive ? undefined : ''}
        onPointerMove={wakeControls}
        onPointerDown={wakeControls}
        onPointerLeave={sleepControls}
        onFocus={wakeControls}
        onKeyDown={wakeControls}
      >
        <Video
          className={styles.video}
          src={src}
          poster={poster}
          playsInline
          preload="metadata"
        />

        <Poster.Root className={styles.poster}>
          <Poster.Image className={styles.posterImage} />
        </Poster.Root>

        <BufferingIndicator
          className={styles.buffering}
          render={(props) => (
            <div {...props}>
              <span className={styles.spinner} />
            </div>
          )}
        />

        {/* Controls.Root renders no element: it is the visibility provider.
            Visibility is always on here, so the bar follows the container's
            own idle state instead: it fades when the pointer leaves the
            player or rests for three seconds. */}
        <Controls.Root visibility="always">
          <Controls.Backdrop className={styles.backdrop} />
          <Controls.Content className={styles.content} data-video-controls>
            <TimeSlider.Root className={styles.timeSlider}>
              <TimeSlider.Track className={styles.track}>
                <TimeSlider.Buffer className={styles.buffer} />
                <TimeSlider.Fill className={styles.fill} />
              </TimeSlider.Track>
              <TimeSlider.Thumb className={styles.thumb} />
            </TimeSlider.Root>

            <Controls.Group className={styles.group} aria-label={controlsLabel}>
              <PlayButton
                render={(props, state) => (
                  <button {...props} className={styles.button}>
                    {state.paused || state.ended ? (
                      <PlayIcon className={styles.icon} />
                    ) : (
                      <PauseIcon className={styles.icon} />
                    )}
                  </button>
                )}
              />

              <MuteButton
                render={(props, state) => (
                  <button {...props} className={styles.button}>
                    {state.volumeLevel === 'off' ? (
                      <VolumeOffIcon className={styles.icon} />
                    ) : state.volumeLevel === 'low' ? (
                      <VolumeLowIcon className={styles.icon} />
                    ) : (
                      <VolumeHighIcon className={styles.icon} />
                    )}
                  </button>
                )}
              />

              {!compact && (
                <VolumeSlider.Root className={styles.volumeSlider}>
                  <VolumeSlider.Track className={styles.volumeTrack}>
                    <VolumeSlider.Fill className={styles.volumeFill} />
                  </VolumeSlider.Track>
                  <VolumeSlider.Thumb className={styles.volumeThumb} />
                </VolumeSlider.Root>
              )}

              <Time.Group className={styles.time}>
                <Time.Value type="current" className={styles.timeValue} />
                <Time.Separator className={styles.timeSeparator} />
                <Time.Value type="duration" className={styles.timeValue} />
              </Time.Group>

              {!compact && (
                <FullscreenButton
                  render={(props, state) => (
                    <button {...props} className={styles.button}>
                      {state.fullscreen ? (
                        <FullscreenExitIcon className={styles.icon} />
                      ) : (
                        <FullscreenEnterIcon className={styles.icon} />
                      )}
                    </button>
                  )}
                />
              )}
            </Controls.Group>
          </Controls.Content>
        </Controls.Root>

        {/* The picture itself is the play button: any click toggles playback
            (no region — the middle third only would miss most of the frame),
            a double click on a side seeks. */}
        <Gesture type="tap" action="togglePaused" pointer="mouse" />
        <Gesture type="tap" action="togglePaused" pointer="touch" />
        <Gesture type="doubletap" action="seekStep" region="left" />
        <Gesture type="doubletap" action="seekStep" region="right" />

        <Hotkey keys="Space" action="togglePaused" />
        <Hotkey keys="k" action="togglePaused" />
        <Hotkey keys="m" action="toggleMuted" />
        {!compact && <Hotkey keys="f" action="toggleFullscreen" />}
        <Hotkey keys="ArrowLeft" action="seekStep" value={-5} />
        <Hotkey keys="ArrowRight" action="seekStep" value={5} />
        <Hotkey keys="ArrowUp" action="volumeStep" value={0.05} />
        <Hotkey keys="ArrowDown" action="volumeStep" value={-0.05} />
      </Container>
    </VideoJsPlayer>
  );
};
